//************************************************************************ /
// EVENT CARD
// Displays information for an upcoming event.
// User can "like" and "view stories" related to it.
//************************************************************************ /

import React from "react";
import {
  Card,
  Divider,
  DecoratedImage,
  Typography,
} from "@dreambigger/design-system/src/components";
import { CardActions } from "@dreambigger/shared/src/components/board/feed-cards/card-components";
import {
  CollectionCommunityFeedItem,
  Image,
  User,
} from "@dreambigger/shared/src/types";
import {
  generateCloudinaryUrl,
  optimizeCloudinaryImg,
} from "@dreambigger/shared/src/utils";
import hover from "../../styles/hover.module.scss";
import anim from "@dreambigger/shared/src/components/board/styles/animations.module.scss";
import { useRouter } from "next/router";
import useMixpanel from "@dreambigger/shared/src/hooks/use-mixpanel";
import InspireAnalytics, {
  InspireAnalyticsHelper,
  InspireAnalyticEvents,
} from "@dreambigger/shared/src/utils/inspire-analytics-helper";
import { useGA, useSegment } from "@dreambigger/shared/src/hooks";

const { Text } = Typography;

type CollectionCardProps = {
  feedItem: CollectionCommunityFeedItem;
  currentUser?: User;
};

const CollectionCard = ({ feedItem, currentUser }: CollectionCardProps) => {
  const {
    id,
    slug,
    title,
    description,
    storyCount,
    images,
    likes,
    iLiked = false,
  } = feedItem;

  // Return nothing if no slug for collection.
  if (!slug) {
    return <></>;
  }

  const collectionUrl = `/collection/${slug}`;

  //Load Analytics
  const googleAnalytics = useGA();
  const mixpanel = useMixpanel();
  const segment = useSegment();
  const router = useRouter();

  const handleCollectionClick = () => {
    router.push(collectionUrl);
  };

  const getCardImage = (images?: Image[]) => {
    if (!images?.[0]) {
      return "";
    }
    if (images[0].imageVersion && images[0].imageId) {
      return generateCloudinaryUrl(images[0].imageVersion, images[0].imageId);
    }
    if (images[0].meta?.src) {
      return optimizeCloudinaryImg(images[0].meta.src);
    }
    return "";
  };

  const handleCardClick = () => {
    // Handle the analytics event where a user clicks the card.
    googleAnalytics.event({
      category: "Card Selection",
      action: "CTA Text:View Stories",
      label: "Card Type:Collection",
      value: 1,
    });
    InspireAnalytics.track({
      eventName: InspireAnalyticEvents.CARD_SELECTED,
      properties: {
        "Card Type": "Collection",
        "Page Type": InspireAnalyticsHelper.getPageType(router.route),
      },
      mixpanel,
      segment,
    });
  };

  return (
    <Card
      onClick={handleCardClick}
      style={{
        boxShadow: "rgba(0, 0, 0, 0.16) 0px 2px 2px",
        cursor: "pointer",
      }}
      bodyStyle={{ padding: 0 }}
      className={`${hover.lift} ${anim.enter_f} mh-auto bg-white`}
      cover={
        images && (
          <DecoratedImage
            alt="Story Img"
            src={getCardImage(images)}
            title={title}
            titleClass="white fwb f-5"
            imgBrightness={title ? 80 : 100}
            onClick={handleCollectionClick}
            loading="lazy"
          />
        )
      }
    >
      {/* CONTENT ------------------------------------------------ */}

      {description && (
        <div onClick={handleCollectionClick}>
          <div className="ph-5 pv-4">
            <Text className="textPrimary">{description}</Text>
          </div>
          <Divider />
        </div>
      )}

      {/* CARD ACTIONS BAR -------------------------------------- */}
      <CardActions
        id={id}
        user={currentUser}
        cardType="collection"
        likeEnabled={true}
        storiesEnabled={true}
        userHasLiked={iLiked}
        likes={likes}
        numOfStories={storyCount}
        ctaTitle={"View Stories"}
        ctaLink={collectionUrl}
      />
    </Card>
  );
};

export default CollectionCard;
